import { Component, Suspense, useEffect, useRef, type ErrorInfo as ReactErrorInfo, type ReactNode } from 'react'
import { Link, Outlet, isRouteErrorResponse, useLocation, useRouteError } from 'react-router-dom'
import { QueryErrorResetBoundary } from '@tanstack/react-query'
import { RefreshCw, TriangleAlert, WifiOff } from 'lucide-react'
import { describeError, errorStatus } from '@/lib/apiError'
import { ErrorState } from './ErrorState'
import { PermissionDenied } from './PermissionDenied'
import { PageSkeleton } from './PageSkeleton'
import { buttonVariants } from './Button'

const CHUNK_RE = /dynamically imported module|Loading chunk|Importing a module script failed/i

/** Stale bundle after a deploy: the only fix is a reload, so say exactly that. */
function ChunkError() {
  return (
    <div role="alert" className="mx-auto flex max-w-lg flex-col items-center px-6 py-16 text-center">
      <div className="mb-4 grid size-12 place-items-center rounded-xl bg-info-subtle text-info-content">
        <RefreshCw className="size-5" />
      </div>
      <h1 className="text-lg font-semibold text-foreground">A newer version is available</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">Reload to continue. Nothing you saved is lost.</p>
      <button type="button" onClick={() => window.location.reload()} className={buttonVariants({ className: 'mt-6' })}>
        Reload
      </button>
    </div>
  )
}

/** Which screen a thrown error deserves. Shared by the area boundary and the route errorElement. */
export function ErrorScreen({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const status = errorStatus(error)
  if (status === 403) {
    // The server's generic role refusal adds nothing; a specific reason (module switched off,
    // platform-level data) is shown so the user knows what to do. Server 403s never name roles.
    const m = (error as { message?: string })?.message ?? ''
    const specific = m && !/^You do not have access to this resource\.?$/i.test(m) ? m : null
    return <PermissionDenied reason={specific} />
  }
  const msg = (error as { message?: string })?.message ?? ''
  if (CHUNK_RE.test(msg)) return <ChunkError />
  if (status) {
    return (
      <ErrorState
        error={error}
        onRetry={onRetry}
        back={{ to: '/dashboard', label: 'Go to dashboard' }}
        title={status === 404 ? 'Record not found' : "This page couldn't load"}
      />
    )
  }
  // No HTTP status: either the network failed, or a render-time bug (TypeError etc.).
  // Never a raw stack on screen — one plain line + Retry.
  const network = describeError(error).kind === 'network'
  return (
    <ErrorState
      error={error}
      onRetry={onRetry}
      back={{ to: '/dashboard', label: 'Go to dashboard' }}
      title={network ? undefined : 'This page hit a problem'}
      message={network ? undefined : 'Something unexpected happened while showing this page. Try again, or go back to the dashboard.'}
    />
  )
}

interface BoundaryProps {
  children: ReactNode
  /** Changing this clears the error (e.g. the pathname: navigating away recovers). */
  resetKey: string
  onReset: () => void
}
interface BoundaryState { error: unknown; key: string }

class Boundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null, key: this.props.resetKey }

  static getDerivedStateFromError(error: unknown): Partial<BoundaryState> {
    return { error: error ?? new Error('Unknown error') }
  }

  static getDerivedStateFromProps(props: BoundaryProps, state: BoundaryState): Partial<BoundaryState> | null {
    return props.resetKey !== state.key ? { error: null, key: props.resetKey } : null
  }

  componentDidCatch(error: unknown, info: ReactErrorInfo) {
    // The one place render/query failures surface for developers; users get the panel.
    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  retry = () => {
    this.props.onReset()
    this.setState({ error: null })
  }

  render() {
    if (this.state.error) return <ErrorScreen error={this.state.error} onRetry={this.retry} />
    return this.props.children
  }
}

/**
 * Area outlet with an error boundary (spec 21 §3.9.3). A failed page query or a render
 * crash is caught HERE, inside the shell, so the sidebar and topbar stay usable. Queries
 * that throw (see `throwOnError` in `@/api/queryClient`) are reset on Retry so only the
 * failed requests refetch; navigating to another page clears the error.
 */
export function AreaOutlet() {
  const { pathname } = useLocation()
  const lastPath = useRef(pathname)
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => {
        // Arriving on a page (again) must retry its failed queries rather than replay the
        // old error; reset() before the children render so their observers refetch.
        if (lastPath.current !== pathname) { lastPath.current = pathname; reset() }
        return (
          <Boundary resetKey={pathname} onReset={reset}>
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </Boundary>
        )
      }}
    </QueryErrorResetBoundary>
  )
}

/** Router `errorElement` — last line of defence for errors outside any area boundary. */
export function RouteErrorPage() {
  const error = useRouteError()
  if (isRouteErrorResponse(error) && error.status === 404) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-background p-6">
        <div className="text-center">
          <h1 className="text-lg font-semibold text-foreground">Page not found</h1>
          <Link to="/dashboard" className={buttonVariants({ className: 'mt-4' })}>Go to dashboard</Link>
        </div>
      </div>
    )
  }
  const msg = (error as { message?: string })?.message ?? ''
  console.error('[RouteError]', error)
  if (isNetworkError(error)) return <NetworkErrorPage />
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-6">
      {CHUNK_RE.test(msg) ? (
        <ChunkError />
      ) : (
        <div role="alert" className="flex max-w-md flex-col items-center text-center">
          <div className="mb-4 grid size-12 place-items-center rounded-xl bg-danger-subtle text-danger-content">
            <TriangleAlert className="size-5" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Something went wrong</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            The app hit an unexpected problem. Reloading usually fixes it.
          </p>
          <div className="mt-6 flex gap-2">
            <button type="button" onClick={() => window.location.reload()} className={buttonVariants()}>Reload</button>
            <a href="/dashboard" className={buttonVariants({ variant: 'outline' })}>Go to dashboard</a>
          </div>
        </div>
      )}
    </div>
  )
}

/** No response at all (offline, DNS, server unreachable) — not a bug in the app. */
export function isNetworkError(e: unknown): boolean {
  const x = e as { code?: string; message?: string; response?: unknown } | null
  if (!x || x.response) return false
  return x.code === 'ERR_NETWORK' || x.message === 'Network Error' || (typeof navigator !== 'undefined' && navigator.onLine === false)
}

/** Offline / server unreachable: say so plainly and retry by itself when the connection returns. */
function NetworkErrorPage() {
  useEffect(() => {
    const back = () => window.location.reload()
    window.addEventListener('online', back)
    return () => window.removeEventListener('online', back)
  }, [])
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background p-6" data-testid="network-error">
      <div role="alert" className="flex max-w-md flex-col items-center text-center">
        <div className="mb-4 grid size-12 place-items-center rounded-xl bg-warning-subtle text-warning-content">
          <WifiOff className="size-5" />
        </div>
        <h1 className="text-lg font-semibold text-foreground">Can&apos;t reach BIGROI</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">
          Check your internet connection. The app will try again as soon as you are back online.
        </p>
        <button type="button" onClick={() => window.location.reload()} className={buttonVariants({ className: 'mt-6' })}>Try again</button>
      </div>
    </div>
  )
}
